import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, ArrowLeft, Check, AlertTriangle, FileText } from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReceivingPage() {
  const [searchParams] = useSearchParams();
  const poIdParam = searchParams.get('po');
  
  const [pos, setPOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receiveData, setReceiveData] = useState({
    location_id: '',
    invoice_number: '',
    invoice_date: '',
    invoice_amount: '',
    notes: '',
    lines: []
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [posRes, locsRes] = await Promise.all([
        fetch(`${API_URL}/api/inv/purchase-orders?status=SENT`, { headers }),
        fetch(`${API_URL}/api/inv/locations`, { headers })
      ]);
      
      if (posRes.ok) {
        const posData = await posRes.json();
        // Also get partially received
        const partialRes = await fetch(`${API_URL}/api/inv/purchase-orders?status=PARTIALLY_RECEIVED`, { headers });
        if (partialRes.ok) {
          const partialData = await partialRes.json();
          setPOs([...posData, ...partialData]);
        } else {
          setPOs(posData);
        }
      }
      if (locsRes.ok) setLocations(await locsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadPODetails = useLatestCallback(async (poId) => {
    try {
      const res = await fetch(`${API_URL}/api/inv/purchase-orders/${poId}`, { headers });
      if (res.ok) {
        const po = await res.json();
        setSelectedPO(po);
        
        // Initialize receive lines
        const lines = po.lines.map(line => ({
          po_line_id: line.id,
          item_id: line.item_id,
          item_name: line.item_name,
          ordered_qty: line.ordered_qty_packs,
          received_so_far: line.received_qty_base || 0,
          received_qty_packs: line.ordered_qty_packs - (line.received_qty_base || 0),
          received_qty_base: line.ordered_qty_packs - (line.received_qty_base || 0),
          damaged_qty: 0,
          price_per_pack: line.price_per_pack
        }));
        
        setReceiveData(prev => ({
          ...prev,
          lines,
          location_id: po.location_id || (locations[0]?.id || '')
        }));
        setShowReceiveDialog(true);
      }
    } catch (err) {
      console.error('Error loading PO:', err);
    }
  });

  useEffect(() => {
    if (poIdParam && pos.length > 0) {
      loadPODetails(poIdParam);
    }
  }, [poIdParam, pos, loadPODetails]);

  const handleReceive = async () => {
    if (!selectedPO || !receiveData.location_id) return;
    
    try {
      const payload = {
        po_id: selectedPO.id,
        location_id: receiveData.location_id,
        invoice_number: receiveData.invoice_number || null,
        invoice_date: receiveData.invoice_date || null,
        invoice_amount: receiveData.invoice_amount ? parseFloat(receiveData.invoice_amount) : null,
        notes: receiveData.notes || null,
        idempotency_key: `recv-${selectedPO.id}-${Date.now()}`,
        lines: receiveData.lines.filter(l => l.received_qty_packs > 0).map(line => ({
          po_line_id: line.po_line_id,
          item_id: line.item_id,
          received_qty_packs: parseFloat(line.received_qty_packs),
          received_qty_base: parseFloat(line.received_qty_base),
          damaged_qty: parseFloat(line.damaged_qty) || 0,
          price_per_pack: parseFloat(line.price_per_pack)
        }))
      };
      
      const res = await fetch(`${API_URL}/api/inv/receiving`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const result = await res.json();
        alert(`Received! Receipt #${result.receipt_number}`);
        setShowReceiveDialog(false);
        setSelectedPO(null);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error receiving goods');
      }
    } catch (err) {
      console.error('Error receiving:', err);
    }
  };

  const updateLineQty = (index, field, value) => {
    const newLines = [...receiveData.lines];
    newLines[index][field] = value;
    if (field === 'received_qty_packs') {
      newLines[index].received_qty_base = value; // 1:1 for now
    }
    setReceiveData({ ...receiveData, lines: newLines });
  };

  const calculateTotal = () => {
    return receiveData.lines.reduce((sum, line) => {
      return sum + (parseFloat(line.received_qty_packs) || 0) * (parseFloat(line.price_per_pack) || 0);
    }, 0);
  };

  return (
    <div className="p-6 space-y-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="receiving-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory/purchase-orders')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Receiving</h1>
          <p className="text-gray-400">Receive goods from purchase orders</p>
        </div>
      </div>

      {/* Pending POs */}
      <Card className="bg-gray-900/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-400" />
            Purchase Orders Ready to Receive
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pos.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No purchase orders pending receipt</p>
          ) : (
            <div className="space-y-3">
              {pos.map(po => (
                <div 
                  key={po.id} 
                  className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => loadPODetails(po.id)}
                >
                  <div className="flex items-center gap-4">
                    <FileText className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="font-medium text-white">{po.po_number}</p>
                      <p className="text-sm text-gray-400">{po.vendor_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-white font-medium">${po.total_amount?.toFixed(2)}</p>
                      <p className="text-sm text-gray-400">{po.line_count} items</p>
                    </div>
                    <Badge className={po.status === 'SENT' ? 'bg-blue-600' : 'bg-yellow-600'}>
                      {po.status}
                    </Badge>
                    <Button size="sm">
                      <Check className="w-4 h-4 mr-1" /> Receive
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receive Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5" />
              Receive {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Location & Invoice Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Receive to Location *</Label>
                <Select
                  value={receiveData.location_id}
                  onValueChange={(v) => setReceiveData({ ...receiveData, location_id: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Invoice Number</Label>
                <Input
                  value={receiveData.invoice_number}
                  onChange={(e) => setReceiveData({ ...receiveData, invoice_number: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="INV-12345"
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <Label className="text-gray-300">Items to Receive</Label>
              <div className="border border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="text-left p-3 text-gray-400 font-medium">Item</th>
                      <th className="text-right p-3 text-gray-400 font-medium">Ordered</th>
                      <th className="text-right p-3 text-gray-400 font-medium">Received</th>
                      <th className="text-right p-3 text-gray-400 font-medium">Receive Now</th>
                      <th className="text-right p-3 text-gray-400 font-medium">Damaged</th>
                      <th className="text-right p-3 text-gray-400 font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiveData.lines.map((line, index) => (
                      <tr key={index} className="border-t border-gray-700">
                        <td className="p-3 text-white">{line.item_name}</td>
                        <td className="p-3 text-right text-gray-400">{line.ordered_qty}</td>
                        <td className="p-3 text-right text-gray-400">{line.received_so_far}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.received_qty_packs}
                            onChange={(e) => updateLineQty(index, 'received_qty_packs', e.target.value)}
                            className="w-20 bg-gray-800 border-gray-700 text-right"
                          />
                        </td>
                        <td className="p-3">
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={line.damaged_qty}
                            onChange={(e) => updateLineQty(index, 'damaged_qty', e.target.value)}
                            className="w-20 bg-gray-800 border-gray-700 text-right"
                          />
                        </td>
                        <td className="p-3 text-right text-white">
                          ${((parseFloat(line.received_qty_packs) || 0) * line.price_per_pack).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-gray-300">Notes</Label>
              <Input
                value={receiveData.notes}
                onChange={(e) => setReceiveData({ ...receiveData, notes: e.target.value })}
                className="bg-gray-800 border-gray-700"
                placeholder="Any notes about this receipt..."
              />
            </div>

            {/* Total & Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-700">
              <span className="text-lg font-semibold text-white">
                Total: ${calculateTotal().toFixed(2)}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleReceive}
                  disabled={!receiveData.location_id || receiveData.lines.every(l => !l.received_qty_packs)}
                  className="bg-green-600 hover:bg-green-500"
                >
                  <Check className="w-4 h-4 mr-2" /> Complete Receiving
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
